export const getProjectWorkspacesListPageUrl = (projectId: string) =>
  `/project/${projectId}/Workspaces?projectId=${projectId}`;

export const getProjectRiskManagementListPageUrl = (projectId: string) =>
  `/project/${projectId}/riskManagement?projectId=${projectId}`;

export const getProjectWorkspacePageUrl = (
  projectId: string,
  spaceId: string,
  spaceType: string = "this",
  reviewType: string = "active",
  path: string[] = [],
  searchText: string = "",
) => {
  if (!projectId) throw new Error("ProjectId is undefined");

  const basePath = `/workspace/${spaceId}/explorer`;

  const searchParams = new URLSearchParams();

  searchParams.set("spaceType", spaceType);
  searchParams.set("projectId", projectId);

  if (reviewType) {
    searchParams.set("type", reviewType);
  }

  if (searchText) {
    searchParams.set("searchText", searchText);
  }

  let url = `${basePath}?${searchParams.toString()}`;
  const filteredPath = path.filter((val: any) => val && val != "0");
  if (filteredPath.length > 0) {
    const folderPath = encodeURIComponent(`/${filteredPath.join("/")}`);
    url += `#folderPath=${folderPath}`;
  }

  return url;
};

export const getProjectIssuesPageUrl = (projectId: string) => `/project/${projectId}/issues?projectId=${projectId}`;
export const getProjectFilesPageUrl = (projectId: string) => `/project/${projectId}/listFiles?projectId=${projectId}`;
export const getProjectTransmittalsPageUrl = (projectId: string) =>
  `/project/${projectId}/listTransmittals?projectId=${projectId}`;
export const getSingleTransmittalPageUrl = (projectId: string, transmittalId: string) =>
  `/project/${projectId}/listTransmittals/${transmittalId}?projectId=${projectId}`;
export const getProjectTasksPageUrl = (projectId: string, taskId?: string) => {
  if (taskId) {
    return `/project/${projectId}/listTask/${taskId}?projectId=${projectId}`;
  }
  return `/project/${projectId}/listTask?projectId=${projectId}`;
};
export const getProjectEstimatesPageUrl = (projectId: string) =>
  `/project/${projectId}/EstimateList?projectId=${projectId}`;
export const getEstimatePageUrl = (projectId: string, estimateId: string) =>
  `/project/${projectId}/estimate/${estimateId}?projectId=${projectId}`;
export const getProjectAssetsPageUrl = (projectId: string) => `/project/${projectId}/assetsList?projectId=${projectId}`;
export const getAssetRelatedDocumentsPageUrl = (projectId: string, assetId: string, assetTag: string) =>
  `/project/${projectId}/assetsList/${assetId}/relatedDocuments?projectId=${projectId}&assetTag=${encodeURIComponent(assetTag)}`;
export const getProjectSchedulesPageUrl = (projectId: string) =>
  `/project/${projectId}/scheduleList?projectId=${projectId}`;
export const getProjectFormsPageUrl = (projectId: string, moduleName: string) =>
  `/project/${projectId}/formsList/${moduleName.toLowerCase()}?selectedTab=list&projectId=${projectId}`;
export const getProjectDashboardPageUrl = (projectId: string) =>
  `/project/${projectId}/projectdashboard?projectId=${projectId}`;
export const getPortalProjectsUrl = (projectId: string) => `/portalSettings/projects/${projectId}/projectUsers`;
export const getUserProjects = (userId: string) => `/portalSettings/users/${userId}/userProjects`;
export const getProjectMailboxPageUrl = (projectId: string) => `/project/${projectId}/mails?projectId=${projectId}`;
export const getProjectReviewsPageUrl = (projectId: string) => `/reviews?projectId=${projectId}&type=active`;
export const getProjectContractPageUrl = (projectId: string) =>
  `/project/${projectId}/contractsList?projectId=${projectId}`;
export const getReviewUrl = (projectId: string, spaceId: string, reviewId: string) =>
  `/review?reviewId=${reviewId}&spaceId=${spaceId}&projectId=${projectId}`;
export const getProjectGeneralClashPageUrl = (projectId: string) =>
  `/project/${projectId}/clashTests?projectId=${projectId}`;
export const getProjectTemplatesUrl = (projectTemplateId?: string) => {
  if (projectTemplateId) {
    return `/portalSettings/projectTemplates/${projectTemplateId}`;
  } else {
    return `/portalSettings/projectTemplates`;
  }
};
export const getCostDatabasesUrl = () => "/portalSettings/costDatabases";
export const getClashDetailsPageUrl = (projectId: string, clashId: string) =>
  `/project/${projectId}/clashDetails/${clashId}?projectId=${projectId}`;

export const getCreateTaskPageUrl = (projectId: string, extraSearchParams?: string) => {
  if (extraSearchParams) {
    return `/project/${projectId}/createTasks?projectId=${projectId}&${extraSearchParams}`;
  }
  return `/project/${projectId}/createTasks?projectId=${projectId}`;
};
export const getEditTaskPageUrl = (projectId: string, taskId: string, extraSearchParams?: string) => {
  if (extraSearchParams) {
    return `/project/${projectId}/editTask/${taskId}?projectId=${projectId}&${extraSearchParams}`;
  }
  return `/project/${projectId}/editTask/${taskId}?projectId=${projectId}`;
};

export const getEstimateUrl = (props: any) => {
  const { estimateList, projectId, estimateId, versionId } = props;
  const searchParams = new URLSearchParams("");
  let location = `/project/${projectId}`;
  if (estimateList) {
    location += "/EstimateList";
  }
  if (estimateId) {
    location += `/estimate/${estimateId}`;

    if (versionId) {
      location += `/version/${versionId}`;
    }
  }

  searchParams.set("projectId", projectId);

  return `${location}?${searchParams.toString()}`;
};

export const getScheduleUrl = (props: any) => {
  const { projectId, scheduleId, selectedTab = "Gantt" } = props;
  const searchParams = new URLSearchParams("");
  const location = `/project/${projectId}/schedule/${scheduleId}/scheduleDetails`;

  searchParams.set("selectedTab", selectedTab);
  searchParams.set("projectId", projectId);

  return `${location}?${searchParams.toString()}`;
};

export const getContractListUrl = (url: any, props: any) => {
  const { projectId, selectedTab } = props;
  const searchParams = new URLSearchParams("");

  if (projectId) {
    searchParams.set("projectId", projectId);
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }

  return `${url}?${searchParams.toString()}`;
};

export const getProjectSettingsPageUrl = ({ projectId, tab, extraSearchParams = {} }: any) => {
  const params = new URLSearchParams({
    projectId,
    tab,
    ...extraSearchParams,
  });
  return `/project/${projectId}/setting?${params.toString()}`;
};

export const getSpaceSettingsPageUrl = ({ projectId, spaceId, tab, extraSearchParams = {} }: any) => {
  const params = new URLSearchParams({
    spaceType: "settings",
    projectId,
    tab: tab || "formFlows",
    type: "active",
    ...extraSearchParams,
  });
  return `/workspace/${spaceId}/explorer?${params.toString()}`;
};

export const getContractDetailUrl = (props: any) => {
  const { selectedTab, projectId, contractId, versionId } = props;

  const url = `/project/${projectId}/contract/${contractId}`;
  const searchParams = new URLSearchParams("");

  if (projectId) {
    searchParams.set("projectId", projectId);
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }
  if (versionId) {
    searchParams.set("versionId", versionId);
  }

  return `${url}?${searchParams.toString()}`;
};

export const getFormsListUrl = (props: any) => {
  const { selectedTab, projectId, resourceType } = props;

  const url = `/project/${projectId}/formsList/${resourceType?.toLowerCase()}`;
  const searchParams = new URLSearchParams("");

  if (projectId) {
    searchParams.set("projectId", projectId);
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }

  return `${url}?${searchParams.toString()}`;
};

export const getProjectPageUrl = (searchParams: string, typeProject: string, viewProject: string) => {
  const params = new URLSearchParams();

  if (searchParams) {
    params.set("search", searchParams);
  }

  if (typeProject) {
    params.set("type", typeProject);
  }
  if (viewProject) {
    params.set("view", viewProject);
  }

  return `/projects?${params.toString()}`;
};
